import { Group, TextInput, Text, Button, ActionIcon, Switch } from '@mantine/core';
import {
  IconSearch,
  IconPlayerPlay,
  IconPlayerPause,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { useSelection } from '@/shared/contexts/SelectionContext';
import type { SearchMode } from '@/lib/types/Media';

interface PlaylistToolbarProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  searchMode: SearchMode;
  onSearchModeChange: (mode: SearchMode) => void;
  totalCount: number;
  resultCount: number;
  onBulkActivate: () => void;
  onBulkDeactivate: () => void;
  onBulkDelete: () => void;
  activateLoading?: boolean;
  deactivateLoading?: boolean;
  deleteLoading?: boolean;
}

export function PlaylistToolbar({
  searchText,
  onSearchChange,
  searchMode,
  onSearchModeChange,
  totalCount,
  resultCount,
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
  activateLoading,
  deactivateLoading,
  deleteLoading,
}: PlaylistToolbarProps) {
  const { selectedCount, clearSelection } = useSelection();

  const hasSelection = selectedCount > 0;
  const isFiltered = searchText.trim().length > 0;

  return (
    <Group justify="space-between">
      <Group>
        <TextInput
          placeholder="Search playlists..."
          leftSection={<IconSearch size={16} />}
          value={searchText}
          onChange={e => onSearchChange(e.currentTarget.value)}
          w={300}
        />

        <Switch
          label="Exact match"
          checked={searchMode === 'exact'}
          onChange={(e) => onSearchModeChange(e.currentTarget.checked ? 'exact' : 'fuzzy')}
          size="sm"
        />

        <Text size="sm" c="dimmed">
          {isFiltered
            ? `${resultCount} of ${totalCount} playlists`
            : `${totalCount} playlists`
          }
        </Text>
      </Group>

      {hasSelection && (
        <Group gap="xs">
          <Text size="sm" fw={500}>
            {selectedCount} selected
          </Text>

          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={onBulkActivate}
            loading={activateLoading}
          >
            Activate
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPause size={14} />}
            onClick={onBulkDeactivate}
            loading={deactivateLoading}
          >
            Deactivate
          </Button>
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={onBulkDelete}
            loading={deleteLoading}
          >
            Delete
          </Button>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={clearSelection}
            aria-label="Clear selection"
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      )}
    </Group>
  );
}
